/**
 * Capture de la PROVENANCE cote navigateur.
 *
 * Google ne transmet pas la requete tapee, mais il transmet le referent et,
 * pour la fiche Google, les parametres `utm_*` du lien « site web ». On fige
 * ces informations a la PREMIERE page vue de la visite, parce qu'une fois que
 * le visiteur navigue, `document.referrer` devient notre propre domaine et
 * l'information d'origine est perdue.
 *
 * STOCKAGE : `sessionStorage`, donc efface a la fermeture de l'onglet. Ce
 * n'est PAS un cookie, ce n'est pas lu par un tiers, ca ne suit personne d'un
 * site a l'autre. La valeur n'est envoyee au serveur que si le visiteur
 * remplit un formulaire ou clique un bouton de contact.
 *
 * TOLERANCE AUX PANNES : en navigation privee ou avec le stockage bloque,
 * chaque lecture/ecriture peut lever. Tout est sous try/catch et le site
 * fonctionne a l'identique sans mesure.
 */

const STORAGE_KEY = "ks-attribution";

export type ClientAttribution = {
  landingPath?: string;
  formPath?: string;
  referrerHost?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
};

const readReferrerHost = (): string | undefined => {
  try {
    const referrer = document.referrer;
    if (!referrer) return undefined;
    const host = new URL(referrer).hostname;
    // Referent interne = ce n'est pas une provenance, c'est notre propre site.
    if (host === window.location.hostname) return undefined;
    return host;
  } catch {
    return undefined;
  }
};

/**
 * A appeler une fois au montage de l'application. Ne fait rien si la visite a
 * deja ete qualifiee (on garde la PREMIERE source, pas la derniere page vue).
 */
export const captureAttribution = (): void => {
  try {
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(STORAGE_KEY)) return;

    const params = new URLSearchParams(window.location.search);
    const attribution: ClientAttribution = {
      landingPath: window.location.pathname,
      referrerHost: readReferrerHost(),
      utmSource: params.get("utm_source") ?? undefined,
      utmMedium: params.get("utm_medium") ?? undefined,
      utmCampaign: params.get("utm_campaign") ?? undefined,
    };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Stockage indisponible : on continue sans mesure.
  }
};

/** Provenance de la visite + page courante, prete a etre postee. */
export const getAttribution = (): ClientAttribution => {
  const formPath =
    typeof window === "undefined" ? undefined : window.location.pathname;
  try {
    if (typeof window === "undefined") return {};
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const stored = raw ? (JSON.parse(raw) as ClientAttribution) : {};
    return { ...stored, formPath };
  } catch {
    return { formPath };
  }
};

/**
 * Signale une action a haute intention (clic telephone, clic WhatsApp).
 *
 * `sendBeacon` survit a la navigation sortante : un `fetch` classique serait
 * annule des que le navigateur ouvre l'application telephone, et le clic ne
 * serait jamais compte.
 */
export const trackIntent = (
  action: "telephone" | "whatsapp" | "reservation-ouverte",
): void => {
  try {
    if (typeof window === "undefined") return;
    const payload = JSON.stringify({ action, attribution: getAttribution() });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        "/api/track",
        new Blob([payload], { type: "application/json" }),
      );
      return;
    }
    void fetch("/api/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    });
  } catch {
    // La mesure ne doit jamais empecher l'appel.
  }
};

/**
 * Ajoute la provenance a un payload de formulaire.
 *
 * C'est CE qui permet de dire « ce devis vient du referencement » : sans lui,
 * un email de demande arrive sans aucune indication de canal ni de page.
 */
export const withAttribution = <T extends object>(
  payload: T,
): T & { attribution: ClientAttribution } => ({
  ...payload,
  attribution: getAttribution(),
});
